import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { cdrAPI, extensionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TIMEZONE_OPTIONS = [
  { label: 'GMT-3 (Chile Summer / Argentina)', value: -180 },
  { label: 'GMT-4 (Santiago de Chile)', value: -240 },
  { label: 'GMT-5 (Colombia / Peru)', value: -300 },
  { label: 'UTC (GMT+0)', value: 0 },
];

const IANA_TZ_MAP = {
  '-180': 'America/Argentina/Buenos_Aires',
  '-240': 'America/Santiago',
  '-300': 'America/Bogota',
  '0': 'UTC',
};

const Dashboard = () => {
  const [cdrs, setCdrs] = useState([]);
  const [stats, setStats] = useState({ total_calls: 0, total_duration: 0, total_cost: 0 });
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registryStats, setRegistryStats] = useState([]);
  const [callEventStats, setCallEventStats] = useState([]);
  const { user } = useAuth();

  const [srcFilter, setSrcFilter] = useState('');
  const [dstFilter, setDstFilter] = useState('');
  const srcRef = useRef(srcFilter);
  const dstRef = useRef(dstFilter);
  useEffect(() => { srcRef.current = srcFilter; }, [srcFilter]);
  useEffect(() => { dstRef.current = dstFilter; }, [dstFilter]);

  const defaultStart = () => {
    const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  };
  const defaultEnd = () => new Date().toISOString().slice(0, 16);

  const [dateStart, setDateStart] = useState(defaultStart);
  const [dateEnd, setDateEnd] = useState(defaultEnd);
  const dateStartRef = useRef(dateStart);
  const dateEndRef = useRef(dateEnd);
  useEffect(() => { dateStartRef.current = dateStart; }, [dateStart]);
  useEffect(() => { dateEndRef.current = dateEnd; }, [dateEnd]);
  const [timezoneOffset, setTimezoneOffset] = useState(() => {
    const saved = localStorage.getItem('tzOffset');
    return saved ? Number(saved) : -240;
  });

  const buildParams = useCallback(() => {
    const params = {};
    if (srcRef.current) params.src = srcRef.current;
    if (dstRef.current) params.dst = dstRef.current;
    if (dateStartRef.current) params.start_date = new Date(dateStartRef.current).toISOString();
    if (dateEndRef.current) params.end_date = new Date(dateEndRef.current).toISOString();
    return params;
  }, []);

  const getGranularity = useCallback(() => {
    const s = dateStartRef.current;
    const e = dateEndRef.current;
    if (!s || !e) return 60;
    const diffMs = new Date(e) - new Date(s);
    const hours = diffMs / (1000 * 60 * 60);
    if (hours <= 1) return 5;
    if (hours <= 6) return 10;
    if (hours <= 24) return 30;
    return 60;
  }, []);

  const doFetch = useCallback(async (showLoading) => {
    if (showLoading) setLoading(true);
    try {
      const params = buildParams();
      if (!showLoading) {
        params.end_date = new Date().toISOString();
      }
      const gran = getGranularity();

      const [cdrsData, statsData, timelineData, registryData, callEventData] = await Promise.all([
        cdrAPI.getCDRs(params),
        cdrAPI.getStats(params),
        cdrAPI.getTimeline({ ...params, granularity: gran }),
        extensionsAPI.getRegistryStatsBySkill(),
        extensionsAPI.getCallEventStatsBySkill(),
      ]);

      setCdrs(cdrsData || []);
      setStats(statsData || { total_calls: 0, total_duration: 0, total_cost: 0 });
      setTimeline(timelineData || []);
      setRegistryStats(registryData || []);
      setCallEventStats(callEventData || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      setCdrs([]);
      setStats({ total_calls: 0, total_duration: 0, total_cost: 0 });
      setTimeline([]);
      setRegistryStats([]);
      setCallEventStats([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [buildParams, getGranularity]);

  useEffect(() => {
    doFetch(true);
    const interval = setInterval(() => doFetch(false), 7000);
    return () => clearInterval(interval);
  }, []);

  const loadData = () => doFetch(true);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const toUTCDate = (s) => new Date(s.endsWith('Z') ? s : s + 'Z');

  const formatDateTZ = (dateString) => {
    if (!dateString) return '';
    const d = toUTCDate(dateString);
    const iana = IANA_TZ_MAP[String(timezoneOffset)] || 'America/Santiago';
    return d.toLocaleString('en-US', { timeZone: iana });
  };

  const handleExportCSV = async () => {
    try {
      const params = buildParams();
      const blob = await cdrAPI.exportCSV(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cdrs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  const handleSearch = () => {
    const s = dateStartRef.current;
    const e = dateEndRef.current;
    if (!s || !e) {
      alert('Please select both start and end dates.');
      return;
    }
    const diffMs = new Date(e) - new Date(s);
    const maxMs = 365 * 24 * 60 * 60 * 1000;
    if (diffMs > maxMs) {
      alert('Maximum allowed range is 1 year. Please reduce the date range.');
      return;
    }
    if (diffMs <= 0) {
      alert('End date must be after start date.');
      return;
    }
    loadData();
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <p>Loading...</p>
      </div>
    );
  }

  const formatTimelineData = () => {
    const gran = getGranularity();
    const iana = IANA_TZ_MAP[String(timezoneOffset)] || 'America/Santiago';
    return timeline.map(item => {
      const d = toUTCDate(item.timestamp);
      if (gran >= 1440) {
        return { ...item, time: d.toLocaleDateString('en-US', { timeZone: iana, month: 'short', day: 'numeric' }) };
      }
      const tzStr = d.toLocaleTimeString('en-US', {
        timeZone: iana,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return { ...item, time: gran >= 60 ? tzStr.replace(/:00$/, '') : tzStr };
    }).sort((a, b) => toUTCDate(a.timestamp) - toUTCDate(b.timestamp));
  };

  const chartData = timeline && timeline.length > 0 ? formatTimelineData() : [];
  const currentGran = getGranularity();
  const granLabel = currentGran >= 60 ? `${currentGran / 60} hr` : `${currentGran} min`;

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <h1 style={styles.pageTitle}>Dashboard</h1>
        <div style={styles.timezoneGroup}>
          <label style={styles.label}>Timezone:</label>
          <select
            value={timezoneOffset}
            onChange={(e) => {
              const val = Number(e.target.value);
              setTimezoneOffset(val);
              localStorage.setItem('tzOffset', String(val));
            }}
            style={styles.select}
          >
            {TIMEZONE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Extension Status by Skill */}
      {registryStats.length > 0 && (
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Extension Status by Skill</h2>
            <button onClick={loadData} style={styles.refreshBtn}>&#x21bb;</button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={registryStats.map(s => ({ name: s.skill, Available: s.AVAILABLE, Busy: s['BUSY-ONCALL'], Unavailable: s.UNAVAILABLE }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9ca3af" tick={{ fontSize: 12 }} />
              <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#e2e8f0' }}
                labelStyle={{ color: '#60a5fa' }}
              />
              <Legend wrapperStyle={{ color: '#e2e8f0' }} />
              <Bar dataKey="Available" fill="#27ae60" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Busy" fill="#fbbf24" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Unavailable" fill="#e74c3c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {callEventStats.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Call Events by Skill</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={callEventStats.map(s => ({ name: s.skill, Answered: s.ANSWER, Ringing: s.RINGING, 'Agent Hangup': s['AGENT-HANGUP'], 'Caller Hangup': s['CALLER-HANGUP'] }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9ca3af" tick={{ fontSize: 12 }} />
              <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#e2e8f0' }}
                labelStyle={{ color: '#60a5fa' }}
              />
              <Legend wrapperStyle={{ color: '#e2e8f0' }} />
              <Bar dataKey="Answered" fill="#27ae60" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Ringing" fill="#60a5fa" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Agent Hangup" fill="#a78bfa" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Caller Hangup" fill="#fb923c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={styles.filterSection}>
        <div style={styles.filterGroup}>
          <label style={styles.label}>ANI (Source):</label>
          <input
            type="text"
            placeholder="e.g. +56226665532"
            value={srcFilter}
            onChange={(e) => setSrcFilter(e.target.value)}
            style={styles.input}
          />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.label}>DNIS (Destination):</label>
          <input
            type="text"
            placeholder="e.g. 975876614"
            value={dstFilter}
            onChange={(e) => setDstFilter(e.target.value)}
            style={styles.input}
          />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.label}>Date Init:</label>
          <input
            type="datetime-local"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            style={styles.input}
          />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.label}>Date End:</label>
          <input
            type="datetime-local"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            style={styles.input}
          />
        </div>
        <button onClick={handleSearch} style={styles.searchButton}>
          Search
        </button>
      </div>

      <div style={styles.chartSection}>
        <h2 style={styles.sectionTitle}>Calls Timeline (Every {granLabel})</h2>
        {chartData && chartData.length > 0 ? (
          <div style={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="time"
                  stroke="#9ca3af"
                  tick={{ fontSize: 12 }}
                  interval={Math.floor(chartData.length / 8)}
                />
                <YAxis
                  stroke="#9ca3af"
                  tick={{ fontSize: 12 }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#e2e8f0'
                  }}
                  labelStyle={{ color: '#60a5fa' }}
                />
                <Legend wrapperStyle={{ color: '#e2e8f0' }} />
                <Line
                  type="monotone"
                  dataKey="calls"
                  stroke="#60a5fa"
                  strokeWidth={2}
                  dot={{ fill: '#60a5fa', r: 3 }}
                  activeDot={{ r: 6, fill: '#60a5fa' }}
                  name={`Calls per ${granLabel}`}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p style={styles.noData}>No timeline data available</p>
        )}
      </div>

      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Total Calls</h3>
          <p style={styles.statValue}>{stats.total_calls}</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Total Duration</h3>
          <p style={styles.statValue}>{formatDuration(stats.total_duration)}</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Total Cost</h3>
          <p style={styles.statValue}>${stats.total_cost.toFixed(2)}</p>
        </div>
      </div>

      <div style={styles.tableSection}>
        <div style={styles.tableHeaderRow}>
          <h2 style={styles.sectionTitle}>Call Detail Records</h2>
          <button onClick={handleExportCSV} style={styles.csvButton}>
            Download CSV
          </button>
        </div>
        {cdrs.length === 0 ? (
          <p>No CDRs found.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>Start Time</th>
                  <th style={styles.th}>Source (ANI)</th>
                  <th style={styles.th}>Destination (DNIS)</th>
                  <th style={styles.th}>Recording ID</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Duration</th>
                  <th style={styles.th}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {cdrs.map((cdr) => (
                  <tr key={cdr._id} style={styles.tableRow}>
                    <td style={styles.td}>{formatDateTZ(cdr.cdr_record.timestamps.start)}</td>
                    <td style={styles.td}>{cdr.cdr_record.source.src}</td>
                    <td style={styles.td}>{cdr.cdr_record.destination.dst}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '12px' }}>{cdr.cdr_record.system_identifiers?.uniqueid || '-'}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: cdr.cdr_record.metrics.direction === 'outbound' ? '#f39c12' : '#3498db'
                      }}>
                        {cdr.cdr_record.metrics.direction === 'outbound' ? 'Outbound' :
                         cdr.cdr_record.metrics.direction === 'inbound' ? 'Inbound' : 'Internal'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor:
                          cdr.cdr_record.metrics.disposition === 'ANSWERED' ? '#27ae60' :
                          cdr.cdr_record.metrics.disposition === 'NO ANSWER' ? '#f39c12' :
                          cdr.cdr_record.metrics.disposition === 'RINGING' ? '#f39c12' :
                          '#e74c3c'
                      }}>
                        {cdr.cdr_record.metrics.disposition === 'ANSWERED' ? 'Answered' :
                         cdr.cdr_record.metrics.disposition === 'NO ANSWER' ? 'No Answer' :
                         cdr.cdr_record.metrics.disposition === 'BUSY' ? 'Busy' :
                         cdr.cdr_record.metrics.disposition === 'FAILED' ? 'Failed' :
                         cdr.cdr_record.metrics.disposition === 'CANCEL' ? 'Cancelled' :
                         cdr.cdr_record.metrics.disposition || 'Unknown'}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDuration(cdr.cdr_record.metrics.duration)}</td>
                    <td style={styles.td}>${(cdr.cdr_record.metrics.cost || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '2rem' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
  pageTitle: { color: '#60a5fa', fontWeight: '700', textShadow: '0 0 10px rgba(96, 165, 250, 0.3)', margin: 0 },
  section: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151', marginBottom: '2rem' },
  sectionTitle: { color: '#60a5fa', marginTop: 0, marginBottom: '1rem', fontWeight: '700' },
  skillGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1rem' },
  skillCard: { backgroundColor: '#111827', padding: '1rem', borderRadius: '8px', border: '1px solid #374151' },
  skillHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' },
  skillName: { color: '#e2e8f0', margin: 0, fontSize: '1rem', fontWeight: '600' },
  skillCounts: { display: 'flex', gap: '0.5rem' },
  countBadge: { padding: '0.2rem 0.5rem', borderRadius: '8px', color: 'white', fontSize: '11px', fontWeight: '600' },
  extensionList: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  extensionRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', borderBottom: '1px solid #1f2937', flexWrap: 'wrap' },
  statusDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  extText: { color: '#e2e8f0', fontWeight: '600', fontSize: '14px', minWidth: '60px' },
  extUser: { color: '#9ca3af', fontSize: '13px', minWidth: '80px' },
  extGroup: { color: '#6b7280', fontSize: '12px', backgroundColor: '#374151', padding: '1px 6px', borderRadius: '4px' },
  extStatusLabel: { fontSize: '12px', fontWeight: '600', marginLeft: 'auto' },
  filterSection: { marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem', backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  label: { fontWeight: '500', color: '#9ca3af', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { padding: '0.5rem 1rem', border: '2px solid #374151', borderRadius: '8px', fontSize: '14px', minWidth: '200px', background: '#111827', color: '#e2e8f0', outline: 'none' },
  select: { padding: '0.5rem 1rem', border: '2px solid #374151', borderRadius: '8px', fontSize: '14px', minWidth: '160px', background: '#111827', color: '#e2e8f0', cursor: 'pointer', outline: 'none' },
  searchButton: { backgroundColor: '#60a5fa', color: 'white', border: 'none', borderRadius: '8px', padding: '0.5rem 1.5rem', cursor: 'pointer', fontSize: '14px', fontWeight: '600', height: '38px' },
  timezoneGroup: { display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-end' },
  statsContainer: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' },
  statCard: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151', color: '#e2e8f0' },
  statTitle: { color: '#9ca3af', fontSize: '14px', margin: '0 0 0.5rem 0', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px' },
  statValue: { color: '#60a5fa', fontSize: '28px', fontWeight: 'bold', margin: 0, textShadow: '0 0 10px rgba(96, 165, 250, 0.3)' },
  tableSection: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151' },
  tableHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  csvButton: { backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', padding: '0.6rem 1.2rem', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  refreshBtn: { backgroundColor: 'transparent', color: '#60a5fa', border: '1px solid #60a5fa', borderRadius: '8px', padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  chartSection: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151', marginBottom: '2rem' },
  chartWrapper: { marginTop: '1rem', marginBottom: '1rem' },
  noData: { color: '#9ca3af', textAlign: 'center', padding: '2rem' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#111827', backgroundImage: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)', borderBottom: '2px solid #60a5fa' },
  th: { padding: '1rem', textAlign: 'left', fontWeight: '600', letterSpacing: '0.3px', color: '#e2e8f0' },
  tableRow: { borderBottom: '1px solid #374151' },
  td: { padding: '1rem', color: '#9ca3af' },
  badge: { padding: '0.25rem 0.75rem', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: '600' },
};

export default Dashboard;
